import React from 'react';
import { Info, Building2, Calculator, FileText, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией об амортизации и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об амортизации: методы, правила, нормативы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-indigo-500" />
          Что такое амортизация?
        </h3>
        <p>
          <strong>Амортизация</strong> — это постепенное перенесение стоимости основных средств по мере их физического и морального износа на стоимость производимой продукции, работ или услуг. Амортизация позволяет организации накапливать средства для последующей замены изношенных объектов.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Методы начисления амортизации
        </h3>
        
        <h4>1. Линейный метод</h4>
        <p>
          Самый распространенный и простой способ. Стоимость основного средства списывается равными долями в течение всего срока службы. Ежегодная сумма амортизации определяется умножением первоначальной стоимости (за вычетом ликвидационной) на норму амортизации.
        </p>
        <p className="text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <strong>Формула:</strong> Годовая амортизация = (Первоначальная стоимость - Ликвидационная стоимость) / Срок полезного использования
        </p>
        
        <h4>2. Метод уменьшаемого остатка</h4>
        <p>
          Ускоренный метод амортизации, при котором большая часть стоимости списывается в первые годы эксплуатации. Норма амортизации увеличивается в 2-3 раза по сравнению с линейным методом, но применяется не к первоначальной стоимости, а к остаточной.
        </p>
        <p className="text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <strong>Формула:</strong> Годовая амортизация = Остаточная стоимость × (Норма амортизации × Коэффициент ускорения)
        </p>
        
        <h4>3. Метод списания стоимости по сумме чисел лет срока полезного использования</h4>
        <p>
          Дегрессивный метод с переменной нормой амортизации. Позволяет списать большую часть стоимости в первые годы использования. Годовая сумма амортизации определяется исходя из первоначальной стоимости и годового соотношения, где в числителе — число лет, оставшихся до конца срока службы, а в знаменателе — сумма чисел лет срока службы.
        </p>
        <p className="text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <strong>Формула:</strong> Годовая амортизация = (Первоначальная стоимость - Ликвидационная стоимость) × (Оставшиеся годы / Сумма чисел лет)
        </p>
        
        <h4>4. Метод списания стоимости пропорционально объему продукции (работ)</h4>
        <p>
          Амортизация начисляется исходя из натурального показателя объема продукции и соотношения первоначальной стоимости к предполагаемому объему продукции за весь срок использования. Подходит для активов, износ которых напрямую связан с частотой их использования.
        </p>
        <p className="text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <strong>Формула:</strong> Годовая амортизация = (Первоначальная стоимость - Ликвидационная стоимость) × (Объем продукции за год / Предполагаемый объем продукции за весь срок)
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Нормативное регулирование в России
        </h3>
        <p>
          Основные документы, регулирующие амортизацию в РФ:
        </p>
        <ul>
          <li><strong>Бухгалтерский учет:</strong> ФСБУ 6/2020 «Основные средства» (заменил ПБУ 6/01 с 2022 года)</li>
          <li><strong>Налоговый учет:</strong> глава 25 Налогового кодекса РФ (статьи 256-259.3)</li>
        </ul>
        
        <p>
          <strong>Амортизационные группы:</strong> В соответствии с Постановлением Правительства РФ от 01.01.2002 № 1 все основные средства относятся к одной из десяти амортизационных групп в зависимости от срока их полезного использования:
        </p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="border border-gray-200 dark:border-gray-700 px-3 py-2">Группа</th>
                <th className="border border-gray-200 dark:border-gray-700 px-3 py-2">Срок полезного использования</th>
                <th className="border border-gray-200 dark:border-gray-700 px-3 py-2">Примеры</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">1</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">1-2 года</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Инструменты, производственный инвентарь</td>
              </tr>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">2</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">2-3 года</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Компьютерная техника, офисное оборудование</td>
              </tr>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">3</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">3-5 лет</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Автотранспорт, оргтехника, мебель</td>
              </tr>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">4</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">5-7 лет</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Машины и оборудование общего применения</td>
              </tr>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">5</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">7-10 лет</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Оборудование специализированное</td>
              </tr>
              <tr>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">6-10</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">10-30+ лет</td>
                <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">Здания, сооружения, передаточные устройства</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium">Особенности налогового учета амортизации в России</h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            В налоговом учете применяются два метода амортизации:
            <br />- <strong>Линейный метод</strong> — обязателен для зданий, сооружений, передаточных устройств (8-10 группы) и основных средств со сроком службы более 20 лет
            <br />- <strong>Нелинейный метод</strong> — может применяться для всех остальных основных средств
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-500" />
          Ускоренная амортизация
        </h3>
        <p>
          Российское законодательство предусматривает возможность применения повышающих коэффициентов (не более 3) к норме амортизации в следующих случаях:
        </p>
        <ul>
          <li>Для основных средств, работающих в условиях агрессивной среды или повышенной сменности</li>
          <li>Для основных средств, являющихся предметом договора лизинга</li>
          <li>Для основных средств, используемых в научно-технической деятельности</li>
          <li>Для объектов, имеющих высокую энергетическую эффективность</li>
          <li>Для основных средств, используемых резидентами особых экономических зон</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при выборе метода амортизации
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Разные методы по-разному влияют на финансовые показатели компании</li>
            <li>Ускоренная амортизация позволяет быстрее обновлять основные фонды</li>
            <li>Линейный метод обеспечивает равномерное распределение расходов</li>
            <li>Расхождения между бухгалтерским и налоговым учетом приводят к образованию временных разниц (ПБУ 18/02)</li>
            <li>Оптимальный выбор метода зависит от бизнес-стратегии, налогового планирования и особенностей актива</li>
          </ul>
        </div>

        <h3>Практические рекомендации</h3>
        <ul>
          <li><strong>Для имущества, подверженного быстрому моральному устареванию</strong> (компьютеры, оргтехника) — метод уменьшаемого остатка</li>
          <li><strong>Для дорогостоящего производственного оборудования</strong> — по сумме чисел лет или уменьшаемого остатка</li>
          <li><strong>Для транспортных средств с интенсивным использованием</strong> — пропорционально объему продукции</li>
          <li><strong>Для зданий, сооружений</strong> — линейный метод</li>
          <li><strong>При стратегии минимизации налоговой нагрузки в первые годы</strong> — ускоренные методы с применением повышающих коэффициентов</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор амортизации поможет вам подобрать оптимальный метод для ваших основных средств, рассчитать амортизационные отчисления и сравнить различные варианты!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;